import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Plus, Shield, Edit, Ban, Users, Eye, Loader2, CheckCircle, MoreHorizontal, X } from 'lucide-react';
import RoleFormDialog from '@/components/clinic/RoleFormDialog';
import { toast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { roleService } from '@/services/roleService';
import { useClinic } from '@/contexts/ClinicContext';
import DeactivateRoleDialog from '@/components/dialog/DeactivateRoleDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import SmartPagination from '@/components/ui/smart-pagination';

// Types
export interface Permission {
  id: string;
  module: string;
  actions: string[];
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: {
    [module: string]: string[];
  };
  isActive: boolean;
  isDefault: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

const RoleManagement = () => {
  const { clinic } = useClinic();
  const [roles, setRoles] = useState<Role[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [viewingRole, setViewingRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [openStatusToggleDialog, setOpenStatusToggleDialog] = useState(false);
  const [roleToToggleStatus, setRoleToToggleStatus] = useState<Role | null>(null);
  
  const itemsPerPage = 10;

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      const response = await roleService.getClinicRoles(clinic?.id, { page: 1, limit: 100 });
      
      const convertedRoles: Role[] = response.data.roles.map(apiRole => {
        const permissions: { [key: string]: string[] } = {};
        
        if (apiRole.rolePermissions) {
          apiRole.rolePermissions.forEach(rp => {
            const moduleName = rp.module.name;
            const permissionName = rp.permission.name;
            
            if (!permissions[moduleName]) {
              permissions[moduleName] = [];
            }
            permissions[moduleName].push(permissionName);
          });
        }
        
        return {
          id: apiRole.id,
          name: apiRole.name,
          description: apiRole.description,
          permissions,
          isActive: apiRole.is_active,
          isDefault: (apiRole as { is_default?: boolean }).is_default ?? false,
          userCount: apiRole.userCount || 0,
          createdAt: new Date(apiRole.created_at).toISOString().split('T')[0],
          updatedAt: new Date(apiRole.updated_at).toISOString().split('T')[0],
        };
      });
      
      setRoles(convertedRoles);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to fetch roles',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRoles = roles.filter(role => {
    const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && role.isActive) ||
      (statusFilter === 'inactive' && !role.isActive);
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const paginatedRoles = filteredRoles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handleCreateRole = () => {
    setEditingRole(null);
    setIsDialogOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setIsDialogOpen(true);
  };

  const handleViewRole = (role: Role) => {
    setViewingRole(role);
  };

  const handleSaveRole = async (roleData: Partial<Role>) => {
    setIsSaving(true);
    try {
      if (editingRole) {
        await roleService.updateClinicRole(clinic?.id, editingRole.id, {
          name: roleData.name!,
          description: roleData.description!,
          permissions: roleData.permissions!,
        });
        
        toast({
          title: 'Role Updated',
          description: `${roleData.name} has been updated successfully.`,
          variant: 'success',
        });
      } else {
        await roleService.createClinicRole(clinic?.id, {
          name: roleData.name!,
          description: roleData.description!,
          permissions: roleData.permissions!,
        });
        
        toast({
          title: 'Role Created',
          description: `${roleData.name} has been created successfully.`,
          variant: 'success',
        });
      }
      
      setIsDialogOpen(false);
      await fetchRoles();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || error.message || `Failed to ${editingRole ? 'update' : 'create'} role`,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getPermissionCount = (permissions: { [key: string]: string[] }) => {
    return Object.values(permissions).reduce((sum, actions) => sum + actions.length, 0);
  };

  useEffect(() => {
    if (clinic?.id) {
      fetchRoles();
    }
  }, [clinic?.id]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-montserrat text-slate-900 tracking-tight text-lg sm:text-2xl" style={{ fontWeight: 700 }}>
            Role & Permission Management
          </h1>
          <p className="text-slate-600 font-inter mt-1 text-xs sm:text-sm">
            Manage roles and permissions for your clinic
          </p>
        </div>
        <Button onClick={handleCreateRole} className="bg-brand-teal hover:bg-brand-teal/90 h-10">
          <Plus className="h-4 w-4" />
          Create Role
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="relative bg-white rounded-xl border border-slate-200 p-3 sm:p-5 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-brand-teal" />
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div>
              <p className="text-[10px] sm:text-sm text-slate-500 font-medium">Active Roles</p>
              <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-0.5 sm:mt-1">
                {roles.filter(r => r.isActive).length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-brand-teal/10 flex items-center justify-center">
              <CheckCircle className="h-4 w-4 sm:h-6 sm:w-6 text-brand-teal" />
            </div>
          </div>
        </div>
        <div className="relative bg-white rounded-xl border border-slate-200 p-3 sm:p-5 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-brand-copper" />
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div>
              <p className="text-[10px] sm:text-sm text-slate-500 font-medium">Total Roles</p>
              <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-0.5 sm:mt-1">
                {roles.length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-brand-copper/10 flex items-center justify-center">
              <Shield className="h-4 w-4 sm:h-6 sm:w-6 text-brand-copper" />
            </div>
          </div>
        </div>
        <div className="relative bg-white rounded-xl border border-slate-200 p-3 sm:p-5 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-blue-500" />
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div>
              <p className="text-[10px] sm:text-sm text-slate-500 font-medium">Users Assigned</p>
              <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-0.5 sm:mt-1">
                {roles.reduce((sum, role) => sum + role.userCount, 0)}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-blue-50 flex items-center justify-center">
              <Users className="h-4 w-4 sm:h-6 sm:w-6 text-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search roles..."
            className="pl-10 h-9"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value.trimStart())}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-36 h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem value="all">
              <span className="flex items-center gap-2">
                All Status
              </span>
            </SelectItem>
            <SelectItem value="active">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-brand-teal" />
                Active
              </span>
            </SelectItem>
            <SelectItem value="inactive">
              <span className="flex items-center gap-2">
                <Ban className="h-4 w-4 text-slate-400" />
                Inactive
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Active Filters */}
      {(statusFilter !== 'all' || searchTerm) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500">Filters:</span>
          {searchTerm && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border">
              Search: {searchTerm}
              <button onClick={() => setSearchTerm('')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {statusFilter !== 'all' && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border capitalize">
              Status: {statusFilter}
              <button onClick={() => setStatusFilter('all')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
            }}
            className="text-xs text-brand-teal hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Roles Table */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                <tr className="border-b border-slate-200">
                  <th className="px-4 py-3 text-left"><Skeleton className="h-3 w-12" /></th>
                  <th className="px-4 py-3 text-left"><Skeleton className="h-3 w-12" /></th>
                  <th className="px-4 py-3 text-left"><Skeleton className="h-3 w-20" /></th>
                  <th className="px-4 py-3 text-left"><Skeleton className="h-3 w-16" /></th>
                  <th className="px-4 py-3 text-left"><Skeleton className="h-3 w-12" /></th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className="border-b border-slate-100">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-lg" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-8" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-8" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-20" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-14 rounded-full" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-8 w-8 rounded" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : paginatedRoles.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-slate-300" strokeWidth={1.5} />
            </div>
            <p className="font-medium text-slate-900 text-sm sm:text-base">No roles found</p>
            <p className="text-slate-500 text-xs sm:text-sm mt-1">
              {searchTerm ? 'Try a different search term' : 'Create your first role to get started'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                <tr className="border-b border-slate-200">
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>Role</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>Users</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>Permissions</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>Updated</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>Status</th>
                  <th className="w-12 px-2 py-3 sticky right-0" style={{ background: 'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)' }}></th>
                </tr>
              </thead>
              <tbody>
                {paginatedRoles.map((role, index) => {
                  const colors = [
                    { bg: 'bg-blue-100', text: 'text-blue-600' },
                    { bg: 'bg-purple-100', text: 'text-purple-600' },
                    { bg: 'bg-pink-100', text: 'text-pink-600' },
                    { bg: 'bg-orange-100', text: 'text-orange-600' },
                    { bg: 'bg-amber-100', text: 'text-amber-600' },
                    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
                    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
                    { bg: 'bg-rose-100', text: 'text-rose-600' },
                    { bg: 'bg-violet-100', text: 'text-violet-600' },
                    { bg: 'bg-sky-100', text: 'text-sky-600' },
                  ];
                  const colorIndex = index % colors.length;
                  const iconColor = colors[colorIndex];

                  return (
                  <tr key={role.id} className="cursor-pointer hover:bg-slate-50/80 transition-colors border-b border-slate-100">
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 ${iconColor.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Shield className={`h-4 w-4 ${iconColor.text}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate flex items-center gap-1.5">
                            {role.name}
                            {role.isDefault && (
                              <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px] px-1.5 py-0">
                                Default
                              </Badge>
                            )}
                          </p>
                          {role.description && (
                            <p className="text-xs text-slate-500 truncate max-w-[200px]">{role.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Users className="h-3.5 w-3.5 text-slate-400" />
                        <span>{role.userCount}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Shield className="h-3.5 w-3.5 text-slate-400" />
                        <span>{getPermissionCount(role.permissions)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <span className="text-slate-500">
                        {new Date(role.updatedAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <Badge
                        className={`text-xs px-2 py-0.5 ${
                          role.isActive
                            ? 'bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/10'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        {role.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="w-12 px-2 py-3 sticky right-0" style={{ background: 'linear-gradient(to right, transparent 0%, white 20%, white 100%)' }}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100">
                            <MoreHorizontal className="h-4 w-4 text-slate-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem
                            onClick={() => handleViewRole(role)}
                            className="cursor-pointer"
                            style={{ fontSize: '13px' }}
                          >
                            <Eye className="h-4 w-4 mr-2 text-slate-500" />
                            View
                          </DropdownMenuItem>
                          {!role.isDefault && (
                            <DropdownMenuItem
                              onClick={() => handleEditRole(role)}
                              className="cursor-pointer"
                              style={{ fontSize: '13px' }}
                            >
                              <Edit className="h-4 w-4 mr-2 text-slate-500" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {!role.isDefault && (
                            <DropdownMenuItem
                              onClick={() => {
                                setRoleToToggleStatus(role);
                                setOpenStatusToggleDialog(true);
                              }}
                              className={`cursor-pointer ${
                                role.isActive ? 'text-red-600' : 'text-brand-teal'
                              }`}
                              style={{ fontSize: '13px' }}
                            >
                              {role.isActive ? (
                                <>
                                  <Ban className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-slate-200 px-2 sm:px-4 py-3 flex flex-col sm:flex-row items-center sm:justify-between gap-3">
            <SmartPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="mx-0 justify-center sm:justify-start"
              maxVisiblePages={3}
            />
            <p className="text-xs text-slate-500 whitespace-nowrap">
              Total: <span className="font-medium text-slate-700">{filteredRoles.length}</span> Roles
            </p>
          </div>
        )}
      </div>

      {/* Role Form Dialog */}
      <RoleFormDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        role={editingRole}
        onSave={handleSaveRole}
        isLoading={isSaving}
      />

      {/* Role Details Dialog */}
      {viewingRole && (
        <Dialog open={!!viewingRole} onOpenChange={() => setViewingRole(null)}>
          <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-xl font-semibold text-slate-900">{viewingRole.name}</DialogTitle>
                  <DialogDescription className="text-sm text-slate-500 mt-0.5">{viewingRole.description}</DialogDescription>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Status</p>
                  <div className="mt-2">
                    <Badge
                      className={`text-xs ${
                        viewingRole.isActive
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {viewingRole.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Users</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">{viewingRole.userCount}</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Created</p>
                  <p className="mt-1 text-sm font-medium text-slate-700">
                    {new Date(viewingRole.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Updated</p>
                  <p className="mt-1 text-sm font-medium text-slate-700">
                    {new Date(viewingRole.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-900">
                  Permissions ({getPermissionCount(viewingRole.permissions)})
                  </span>
                </div>
                <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                  {Object.entries(viewingRole.permissions).map(([module, actions]) => (
                    <div
                      key={module}
                      className="p-4 border border-slate-200 rounded-xl bg-white"
                    >
                      <div className="font-medium text-slate-900 text-sm capitalize mb-3">
                        {module.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {actions.map(action => (
                          <span
                            key={action}
                            className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 capitalize"
                          >
                            {action.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setViewingRole(null)} className="border-slate-200">
                Close
              </Button>
              {!viewingRole.isDefault && (
                <Button
                  onClick={() => {
                    setViewingRole(null);
                    handleEditRole(viewingRole);
                  }}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  <Edit className="h-4 w-4" />
                  Edit Role
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Role Status Toggle Confirmation Dialog */}
      <DeactivateRoleDialog
        open={openStatusToggleDialog}
        onOpenChange={setOpenStatusToggleDialog}
        role={roleToToggleStatus || null}
        onSuccess={(role: Role) => {
          setOpenStatusToggleDialog(false);
          setRoles(prevRoles =>
            prevRoles.map(r =>
              r.id === role.id ? { ...r, isActive: !r.isActive } : r
            )
          );
        }}
      />
    </div>
  );
};

export default RoleManagement;
